/**
 * The auth client, in context — for the same reason the API client is.
 *
 * `.docs/reports/03-report.md`, on the API: "Nothing imports a global." The SPA
 * holds one `authClient` because Better Auth's cookie cache and nanostores are
 * per-instance (`client.ts` says why), but a component that reached for it
 * directly would drag a real socket into every test that renders that component —
 * and the sidebar, which signs you out and switches your client, is the shell.
 *
 * `main.tsx` puts the real one here; `src/test/render.tsx` puts `fakeAuthClient()`
 * here, which is why a test can assert that switching organisation called
 * `setActive` without a server.
 */

import { createContext, use, type ReactNode } from 'react'

import type { PortalAuthClient } from './client.js'

const AuthContext = createContext<PortalAuthClient | null>(null)

export function AuthProvider({
  client,
  children,
}: {
  readonly client: PortalAuthClient
  readonly children: ReactNode
}) {
  return <AuthContext value={client}>{children}</AuthContext>
}

export function useAuth(): PortalAuthClient {
  const client = use(AuthContext)
  if (client === null) {
    throw new Error(
      'useAuth() outside an <AuthProvider>. providers.tsx and test/render.tsx put one there.',
    )
  }
  return client
}
